/**
 * Working Memory — The Grid
 *
 * 5 game types (Order, Swap, Remove, Add, Master). Each round = TWO phases:
 *   1. ENCODE — show the items (staggered or simultaneous, per Config)
 *   2. RECALL — show the post-change state, user taps, recall timer counts down
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Play, Clock, Trophy, ArrowLeft, RotateCcw, Sparkles,
  ListOrdered, ArrowLeftRight, Eraser, PlusCircle, Shuffle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Header from "../components/Header";
import TutorialVideoSection from "../components/TutorialVideoSection";
import TrainingErrorBoundary from "../components/TrainingErrorBoundary";
import RatingPanel from "../components/RatingPanel";
import AchievementUnlocked from "../components/AchievementUnlocked";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const PHASES = {
  CONFIG: "config",
  ENCODE: "encode",
  REVIEW: "review",   // Remove-only intermediate phase: "here's what's still here"
  RECALL: "recall",
  RESULTS: "results",
};

const DURATION_OPTIONS = [
  { value: 60, label: "60s", tag: "QUICK" },
  { value: 120, label: "120s", tag: "STANDARD" },
  { value: 180, label: "180s", tag: "FOCUSED" },
  { value: 300, label: "300s", tag: "DEEP" },
];

const SPEED_OPTIONS = [
  { value: 500, label: "0.5s", tag: "FAST" },
  { value: 1000, label: "1.0s", tag: "STANDARD" },
  { value: 2000, label: "2.0s", tag: "SLOWER" },
];

const RECALL_OPTIONS = [
  { value: 5, label: "5s", tag: "FAST" },
  { value: 10, label: "10s", tag: "STANDARD" },
  { value: 20, label: "20s", tag: "RELAXED" },
  { value: 0, label: "∞", tag: "NO LIMIT" },
];

const GAME_OPTIONS = [
  { value: "order",  label: "Order",  icon: ListOrdered,    blurb: "Tap items in the order they appeared",       lock_reveal: "staggered" },
  { value: "swap",   label: "Swap",   icon: ArrowLeftRight, blurb: "Tap every cell whose item moved" },
  { value: "remove", label: "Remove", icon: Eraser,         blurb: "Tap the items that were removed" },
  { value: "add",    label: "Add",    icon: PlusCircle,     blurb: "Tap the new items" },
  { value: "master", label: "Master", icon: Shuffle,        blurb: "Random mix of all four games" },
];

const REVEAL_OPTIONS = [
  { value: "staggered",    label: "One by one", tag: "BUILDS SEQUENCE" },
  { value: "simultaneous", label: "All at once", tag: "FULL FLASH" },
];

const ICON_BASE = "/wm-items";

const fmtMs = (ms) => `${(ms / 1000).toFixed(1)}s`;
const fmtSec = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

const PHASE_TITLE = {
  order:  { encode: "Remember the order",     review: null,                       recall: "Tap items in the original order" },
  swap:   { encode: "Memorise the positions", review: null,                       recall: "Tap every cell whose item moved" },
  remove: { encode: "Memorise the items",     review: "What items were removed?",  recall: "Tap the items that were removed" },
  add:    { encode: "Memorise the items",     review: null,                       recall: "Tap the new items" },
};

// ------------------------------------------------------------
// Config screen
// ------------------------------------------------------------
const Tile = ({ active, onClick, label, tag, testid, icon: Icon, blurb }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`relative px-4 py-5 rounded-xl border-2 transition-all text-left ${
      active
        ? "border-primary bg-primary/10 shadow-[0_0_20px_-4px_var(--color-primary)]"
        : "border-border hover:border-primary/40 bg-card"
    }`}
  >
    {Icon ? (
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
        <div className="font-heading text-lg font-bold">{label}</div>
      </div>
    ) : (
      <div className="font-heading text-2xl font-bold">{label}</div>
    )}
    {blurb && <div className="text-xs text-muted-foreground leading-snug">{blurb}</div>}
    {tag && <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{tag}</div>}
  </button>
);

const ConfigScreen = ({
  gameType, setGameType,
  duration, setDuration,
  speed, setSpeed,
  recall, setRecall,
  reveal, setReveal,
  loading, onStart,
}) => {
  const currentGame = GAME_OPTIONS.find((g) => g.value === gameType);
  const revealLocked = currentGame?.lock_reveal === "staggered";
  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Brain className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-1">
              Working Memory · The Grid
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Configure training</h1>
          </div>
        </div>
        <p className="text-muted-foreground max-w-2xl">
          Pick a game, then how long the session runs, how fast items show, and how long you have to recall.
        </p>
      </div>

      {/* Game type */}
      <Card className="mb-6 bg-card/50 border-border">
        <CardContent className="p-6 sm:p-8">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Game</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wm-game-grid">
            {GAME_OPTIONS.map((g) => (
              <Tile
                key={g.value}
                active={gameType === g.value}
                onClick={() => setGameType(g.value)}
                label={g.label}
                blurb={g.blurb}
                icon={g.icon}
                testid={`wm-game-${g.value}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Duration */}
      <Card className="mb-6 bg-card/50 border-border">
        <CardContent className="p-6 sm:p-8">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Session duration</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="wm-duration-grid">
            {DURATION_OPTIONS.map((opt) => (
              <Tile key={opt.value} active={duration === opt.value} onClick={() => setDuration(opt.value)}
                label={opt.label} tag={opt.tag} testid={`wm-duration-${opt.value}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Memorisation speed */}
      <Card className="mb-6 bg-card/50 border-border">
        <CardContent className="p-6 sm:p-8">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Memorisation speed</h3>
          <p className="text-xs text-muted-foreground mb-4">How long each item shows during the memorise phase.</p>
          <div className="grid grid-cols-3 gap-3" data-testid="wm-speed-grid">
            {SPEED_OPTIONS.map((opt) => (
              <Tile key={opt.value} active={speed === opt.value} onClick={() => setSpeed(opt.value)}
                label={opt.label} tag={opt.tag} testid={`wm-speed-${opt.value}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recall time */}
      <Card className="mb-6 bg-card/50 border-border">
        <CardContent className="p-6 sm:p-8">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Recall time</h3>
          <p className="text-xs text-muted-foreground mb-4">How long you have to tap your answers in the recall phase.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="wm-recall-grid">
            {RECALL_OPTIONS.map((opt) => (
              <Tile key={opt.value} active={recall === opt.value} onClick={() => setRecall(opt.value)}
                label={opt.label} tag={opt.tag} testid={`wm-recall-${opt.value}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Reveal style — locked to staggered for Order */}
      <Card className="mb-8 bg-card/50 border-border">
        <CardContent className="p-6 sm:p-8">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Reveal style</h3>
          <p className="text-xs text-muted-foreground mb-4">
            {revealLocked
              ? "Order is always one-by-one (the sequence is the whole point)."
              : "Stagger items for memorisation rhythm, or flash them all together."}
          </p>
          <div className={`grid grid-cols-2 gap-3 ${revealLocked ? "opacity-50 pointer-events-none" : ""}`} data-testid="wm-reveal-grid">
            {REVEAL_OPTIONS.map((opt) => (
              <Tile
                key={opt.value}
                active={(revealLocked ? "staggered" : reveal) === opt.value}
                onClick={() => !revealLocked && setReveal(opt.value)}
                label={opt.label} tag={opt.tag}
                testid={`wm-reveal-${opt.value}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Button
        data-testid="wm-start-btn"
        size="lg"
        onClick={onStart}
        disabled={loading}
        className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base px-10 py-6"
      >
        <Play className="w-5 h-5 mr-2" />
        {loading ? "Loading..." : "Start training"}
      </Button>

      <div className="mt-12">
        <TutorialVideoSection section="working_memory" sectionLabel="Working Memory" />
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// Grid renderer
// ------------------------------------------------------------
const Grid = ({ rows, cols, state, glowCells, glowColor, onCellClick, selectedCells, sequenceMap, hideItems }) => {
  const cells = [];
  for (let i = 0; i < rows * cols; i++) {
    const icon = state[i];
    const isGlowing = glowCells?.includes(i);
    const selectionIdx = selectedCells?.indexOf(i);
    const isSelected = selectionIdx !== undefined && selectionIdx >= 0;
    const sequenceNumber = sequenceMap ? sequenceMap[i] : null;
    cells.push(
      <button
        key={i}
        data-testid={`wm-cell-${i}`}
        type="button"
        onClick={onCellClick ? () => onCellClick(i) : undefined}
        disabled={!onCellClick}
        className={`relative aspect-square rounded-xl border-2 flex items-center justify-center transition-all ${
          isGlowing
            ? glowColor === "purple"
              ? "border-purple-500 shadow-[0_0_30px_-2px_rgba(139,92,246,0.8)] bg-purple-500/10"
              : "border-lime-400 shadow-[0_0_30px_-2px_rgba(190,242,100,0.8)] bg-lime-400/10"
            : isSelected
            ? "border-pink-500 bg-pink-500/10 shadow-[0_0_20px_-4px_rgba(236,72,153,0.6)]"
            : "border-border bg-card/40 hover:border-primary/30"
        } ${onCellClick ? "cursor-pointer" : "cursor-default"}`}
        style={{ touchAction: "manipulation" }}
      >
        {icon && !hideItems && (
          <motion.img
            key={`${i}-${icon}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            src={`${ICON_BASE}/${icon}.png`}
            alt={icon}
            className="w-[70%] h-[70%] object-contain pointer-events-none select-none"
            draggable={false}
          />
        )}
        {sequenceNumber != null && (
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-pink-500 text-white text-xs font-bold flex items-center justify-center shadow-lg">
            {sequenceNumber}
          </div>
        )}
        <span className="absolute bottom-1 right-2 text-[9px] font-mono text-muted-foreground/40 select-none">{i + 1}</span>
      </button>
    );
  }
  return (
    <div
      className="grid gap-3 mx-auto"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        maxWidth: `${Math.min(640, cols * 110)}px`,
      }}
      data-testid="wm-grid"
    >
      {cells}
    </div>
  );
};

// ------------------------------------------------------------
// Phase header
// ------------------------------------------------------------
const PhaseHeader = ({ gameType, phaseLabel, level, gridLabel, info, sessionTimeLeft, recallSecondsLeft }) => (
  <div className="max-w-4xl mx-auto px-6 pt-6">
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-widest text-purple-400 mb-1">
          {gameType?.toUpperCase()} · {phaseLabel}
        </p>
        <p className="text-xs font-mono text-muted-foreground">
          Lvl {level} · {gridLabel}{info ? ` · ${info}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-4">
        {recallSecondsLeft != null && (
          <div className="flex items-center gap-2 text-pink-400 font-mono text-xl font-bold" data-testid="wm-recall-timer">
            <Clock className="w-5 h-5" />
            <span>{recallSecondsLeft}s</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-orange-500 font-mono text-xl font-bold">
          <Clock className="w-5 h-5" />
          <span data-testid="wm-session-timer">{fmtSec(sessionTimeLeft)}</span>
        </div>
      </div>
    </div>
  </div>
);

// ------------------------------------------------------------
// Round runner — drives encode + recall
// ------------------------------------------------------------
const RoundRunner = ({ round, sessionTimeLeft, onRoundComplete }) => {
  const [phase, setPhase] = useState(PHASES.ENCODE);
  const [gridState, setGridState] = useState({});
  const [glowCells, setGlowCells] = useState([]);
  const [taps, setTaps] = useState([]);
  const [locked, setLocked] = useState(false);
  const [recallSecondsLeft, setRecallSecondsLeft] = useState(round.recall_time_seconds || 10);
  const [feedback, setFeedback] = useState(null);
  const startedAt = useRef(0);

  const speedMs = round.item_speed_ms || 1000;
  const reveal = round.reveal_style || "staggered";
  const expected = round.expected_taps || 0;
  const ordered = round.answer_ordered;

  // ---- log phase transitions for easy debugging ----
  useEffect(() => {
     
    console.log("[WM] phase ->", phase, {
      round_id: round.round_id,
      game_type: round.game_type,
      level: round.level,
      expected,
    });
  }, [phase, round, expected]);

  // ---- ENCODE: place items per reveal_style ----
  useEffect(() => {
    if (phase !== PHASES.ENCODE) return;
    let cancelled = false;
    const doEncode = async () => {
      const order = round.encode_order || [];

      if (reveal === "simultaneous") {
        // Flash all at once, hold for items.length × speed/2, then go to recall
        const flashed = Object.fromEntries(order.map(({ cell, icon }) => [cell, icon]));
        setGridState(flashed);
        setGlowCells(order.map((o) => o.cell));
        const hold = Math.max(800, order.length * speedMs * 0.6);
        await new Promise((r) => setTimeout(r, hold));
        if (cancelled) return;
        setGlowCells([]);
      } else {
        // Staggered: one by one
        const built = {};
        for (let i = 0; i < order.length; i++) {
          if (cancelled) return;
          const { cell, icon } = order[i];
          built[cell] = icon;
          setGridState({ ...built });
          setGlowCells([cell]);
          await new Promise((r) => setTimeout(r, speedMs));
        }
        if (cancelled) return;
        setGlowCells([]);
      }

      // brief pause before transitioning to the next phase
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;

      // If this round has a REVIEW phase (Remove), show it next at the same
      // timing/style as encode. Otherwise jump straight to RECALL.
      const reviewItems = round.review_state || [];
      if (reviewItems.length > 0) {
        setGridState({});           // clear before review animation
        setGlowCells([]);
        setPhase(PHASES.REVIEW);
        return;
      }

      const recallState = Object.fromEntries(
        (round.recall_state || []).map(({ cell, icon }) => [cell, icon])
      );
      setGridState(recallState);
      setTaps([]);
      setLocked(false);
      setRecallSecondsLeft(round.recall_time_seconds || 10);
      startedAt.current = performance.now();
      setPhase(PHASES.RECALL);
    };
    doEncode();
    return () => { cancelled = true; };
     
  }, [phase]);

  // ---- REVIEW: replay the "what's still here" state, same speed as encode ----
  useEffect(() => {
    if (phase !== PHASES.REVIEW) return;
    let cancelled = false;
    const doReview = async () => {
      const items = round.review_state || [];
      if (reveal === "simultaneous") {
        const flashed = Object.fromEntries(items.map(({ cell, icon }) => [cell, icon]));
        setGridState(flashed);
        setGlowCells(items.map((o) => o.cell));
        const hold = Math.max(800, items.length * speedMs * 0.6);
        await new Promise((r) => setTimeout(r, hold));
        if (cancelled) return;
        setGlowCells([]);
      } else {
        const built = {};
        for (let i = 0; i < items.length; i++) {
          if (cancelled) return;
          const { cell, icon } = items[i];
          built[cell] = icon;
          setGridState({ ...built });
          setGlowCells([cell]);
          await new Promise((r) => setTimeout(r, speedMs));
        }
        if (cancelled) return;
        setGlowCells([]);
      }
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;

      // Now load the phase-3 recognition grid
      const recallState = Object.fromEntries(
        (round.recall_state || []).map(({ cell, icon }) => [cell, icon])
      );
      setGridState(recallState);
      setTaps([]);
      setLocked(false);
      setRecallSecondsLeft(round.recall_time_seconds || 10);
      startedAt.current = performance.now();
      setPhase(PHASES.RECALL);
    };
    doReview();
    return () => { cancelled = true; };
     
  }, [phase]);

  // ---- RECALL timer (only if recall_time > 0) ----
  useEffect(() => {
    if (phase !== PHASES.RECALL) return;
    if (!round.recall_time_seconds || round.recall_time_seconds <= 0) return;
    if (locked) return;
    const id = setInterval(() => {
      setRecallSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          // Auto-submit whatever the user has tapped so far
          setLocked((wasLocked) => {
            if (!wasLocked) submitTaps();
            return true;
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
     
  }, [phase, locked]);

  // ---- Auto-submit when user has hit the expected tap count ----
  useEffect(() => {
    if (phase !== PHASES.RECALL) return;
    if (locked) return;
    if (expected > 0 && taps.length >= expected) {
      setLocked(true);
      submitTaps();
    }
     
  }, [taps, phase, locked, expected]);

  const submitTaps = useCallback(async () => {
    const reactionMs = startedAt.current ? performance.now() - startedAt.current : 0;
    try {
      const res = await authFetch(`${API}/wm/round/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: round.session_id,
          round_id: round.round_id,
          taps,
          reaction_time_ms: reactionMs,
        }),
      });
      if (!res.ok) throw new Error("submit failed");
      const data = await res.json();
       
      console.log("[WM] round result", data);
      setFeedback({
        correct: data.correct,
        expected: data.expected,
        nextLevel: data.next_level,
        maxLevel: data.max_level,
      });
      setTimeout(() => onRoundComplete(data), 1700);
    } catch (e) {
      toast.error("Could not submit round");
      onRoundComplete({ correct: false, next_level: round.level, max_level: round.level });
    }
  }, [taps, round, onRoundComplete]);

  const handleClick = (cell) => {
    if (locked) return;
    setTaps((prev) =>
      prev.includes(cell) ? prev.filter((c) => c !== cell) : [...prev, cell]
    );
  };

  const sequenceMap = useMemo(() => {
    if (!ordered) return null;
    const m = {};
    taps.forEach((c, i) => { m[c] = i + 1; });
    return m;
  }, [taps, ordered]);

  const grid = round.grid;
  const gameType = round.game_type;
  const titleSet = PHASE_TITLE[gameType] || PHASE_TITLE.swap;
  const hideItems = false;

  if (phase === PHASES.ENCODE) {
    return (
      <>
        <PhaseHeader
          gameType={gameType}
          phaseLabel="MEMORISE"
          level={round.level}
          gridLabel={`${grid.rows}×${grid.cols} grid`}
          info={`${round.encode_order.length} items`}
          sessionTimeLeft={sessionTimeLeft}
        />
        <div className="max-w-4xl mx-auto px-6 pb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-8 text-lime-400" data-testid="wm-phase-title">
            {titleSet.encode}
          </h2>
          <Grid
            rows={grid.rows}
            cols={grid.cols}
            state={gridState}
            glowCells={glowCells}
            glowColor="lime"
          />
        </div>
      </>
    );
  }

  if (phase === PHASES.REVIEW) {
    return (
      <>
        <PhaseHeader
          gameType={gameType}
          phaseLabel="WHAT'S LEFT"
          level={round.level}
          gridLabel={`${grid.rows}×${grid.cols} grid`}
          info={`${(round.review_state || []).length} items remain`}
          sessionTimeLeft={sessionTimeLeft}
        />
        <div className="max-w-4xl mx-auto px-6 pb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-8 text-purple-400" data-testid="wm-phase-title">
            {titleSet.review || "What items were removed?"}
          </h2>
          <Grid
            rows={grid.rows}
            cols={grid.cols}
            state={gridState}
            glowCells={glowCells}
            glowColor="purple"
          />
        </div>
      </>
    );
  }

  // RECALL
  return (
    <>
      <PhaseHeader
        gameType={gameType}
        phaseLabel="RECALL"
        level={round.level}
        gridLabel={`${grid.rows}×${grid.cols} grid`}
        info={`${taps.length} / ${expected} tapped`}
        sessionTimeLeft={sessionTimeLeft}
        recallSecondsLeft={round.recall_time_seconds > 0 ? recallSecondsLeft : null}
      />
      <div className="max-w-4xl mx-auto px-6 pb-12">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-8 text-pink-400" data-testid="wm-phase-title">
          {titleSet.recall}
        </h2>
        <Grid
          rows={grid.rows}
          cols={grid.cols}
          state={gridState}
          onCellClick={locked ? null : handleClick}
          selectedCells={taps}
          sequenceMap={sequenceMap}
          hideItems={hideItems}
        />
      </div>
      {feedback && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed inset-x-0 bottom-8 flex justify-center z-30"
        >
          {(() => {
            // Decide whether the user actually levelled up, stayed, or dropped.
            // Server returns next_level for the upcoming round. If correct and
            // already at the ceiling (10), we hold instead of levelling up.
            const isCorrect = feedback.correct;
            const levelled = feedback.nextLevel > round.level;
            const dropped = feedback.nextLevel < round.level;
            const held = !levelled && !dropped;
            const passStyle = "bg-primary/20 border-primary text-primary";
            const failStyle = "bg-orange-500/20 border-orange-500 text-orange-400";
            let message;
            if (isCorrect && levelled) {
              message = `Correct — levelling up to Lvl ${feedback.nextLevel}`;
            } else if (isCorrect && held) {
              message = `Correct — holding at Lvl ${round.level} (max)`;
            } else if (!isCorrect && dropped) {
              message = `Not quite — dropping to Lvl ${feedback.nextLevel}`;
            } else {
              // Held at floor (lvl 1) after a miss.
              message = `Not quite — staying at Lvl ${round.level}`;
            }
            return (
              <div
                data-testid="wm-round-feedback"
                className={`px-8 py-4 rounded-2xl border-2 backdrop-blur-md ${isCorrect ? passStyle : failStyle}`}
              >
                <p className="font-bold text-lg">{message}</p>
              </div>
            );
          })()}
        </motion.div>
      )}
    </>
  );
};

// ------------------------------------------------------------
// Main page
// ------------------------------------------------------------
const WorkingMemoryTraining = ({ user }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASES.CONFIG);
  const [gameType, setGameType] = useState("master");
  const [duration, setDuration] = useState(120);
  const [speed, setSpeed] = useState(1000);
  const [recall, setRecall] = useState(10);
  const [reveal, setReveal] = useState("staggered");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [round, setRound] = useState(null);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [results, setResults] = useState(null);
  const sessionEndsAt = useRef(0);
  const finishedRef = useRef(false);

  // Session-wide countdown
  useEffect(() => {
    if (![PHASES.ENCODE, PHASES.REVIEW, PHASES.RECALL].includes(phase)) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((sessionEndsAt.current - Date.now()) / 1000));
      setSessionTimeLeft(remaining);
      if (remaining <= 0 && !finishedRef.current) {
        finishedRef.current = true;
        finishSession();
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
     
  }, [phase]);

  const startSession = async () => {
    setLoading(true);
    finishedRef.current = false;
    try {
      const res = await authFetch(`${API}/wm/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          duration_seconds: duration,
          item_speed_ms: speed,
          game_type: gameType,
          reveal_style: gameType === "order" ? "staggered" : reveal,
          recall_time_seconds: recall,
        }),
      });
      if (!res.ok) throw new Error("start failed");
      const data = await res.json();
      setSession(data);
      sessionEndsAt.current = Date.now() + duration * 1000;
      setSessionTimeLeft(duration);
      await loadNextRound(data.session_id);
    } catch (e) {
      toast.error("Could not start training");
      setLoading(false);
    }
  };

  const loadNextRound = async (sessionId) => {
    try {
      const res = await authFetch(`${API}/wm/round`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!res.ok) throw new Error("round failed");
      const data = await res.json();
      setRound({ ...data, session_id: sessionId });
      setPhase(PHASES.ENCODE);
      setLoading(false);
    } catch (e) {
      toast.error("Could not load next round");
      setLoading(false);
    }
  };

  const handleRoundComplete = useCallback(() => {
    if (finishedRef.current) return;
    if (sessionEndsAt.current - Date.now() <= 1500) {
      finishedRef.current = true;
      finishSession();
      return;
    }
    if (session) loadNextRound(session.session_id);
     
  }, [session]);

  const finishSession = async () => {
    if (!session) return;
    try {
      // Compute local start-of-week (Monday 00:00) and start-of-month for
      // the server-side personal-best comparison across time windows.
      const startOfWeek = (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        const dow = d.getDay();
        d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
        return d.toISOString();
      })();
      const startOfMonth = (() => {
        const d = new Date();
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      })();
      const res = await authFetch(`${API}/wm/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: session.session_id,
          start_of_week_iso: startOfWeek,
          start_of_month_iso: startOfMonth,
        }),
      });
      if (!res.ok) throw new Error("finish failed");
      const data = await res.json();
      setResults(data);
      setPhase(PHASES.RESULTS);
    } catch (e) {
      toast.error("Could not finalise session");
      setPhase(PHASES.RESULTS);
    }
  };

  const restart = () => {
    setSession(null);
    setRound(null);
    setResults(null);
    setSessionTimeLeft(0);
    setPhase(PHASES.CONFIG);
    finishedRef.current = false;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />

      {phase === PHASES.CONFIG && (
        <ConfigScreen
          gameType={gameType} setGameType={setGameType}
          duration={duration} setDuration={setDuration}
          speed={speed} setSpeed={setSpeed}
          recall={recall} setRecall={setRecall}
          reveal={reveal} setReveal={setReveal}
          loading={loading}
          onStart={startSession}
        />
      )}

      {[PHASES.ENCODE, PHASES.REVIEW, PHASES.RECALL].includes(phase) && round && (
        <TrainingErrorBoundary onReset={restart}>
          <AnimatePresence mode="wait">
            <motion.div
              key={round.round_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <RoundRunner
                round={round}
                sessionTimeLeft={sessionTimeLeft}
                onRoundComplete={handleRoundComplete}
              />
            </motion.div>
          </AnimatePresence>
        </TrainingErrorBoundary>
      )}

      {phase === PHASES.RESULTS && results && (
        <ResultsScreen results={results} duration={duration} speed={speed} onRestart={restart} navigate={navigate} />
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Results screen
// ------------------------------------------------------------
const ResultsScreen = ({ results, duration, speed, onRestart, navigate }) => {
  const cards = [
    { label: "ROUNDS PLAYED", value: results.rounds_played },
    { label: "ACCURACY", value: `${Math.round(results.accuracy)}%`, sub: `${results.rounds_correct} of ${results.rounds_played} rounds` },
    { label: "MAX LEVEL", value: results.max_level, sub: `Reached level ${results.max_level} of 10` },
    { label: "AVG REACTION", value: results.avg_reaction_ms ? fmtMs(results.avg_reaction_ms) : "—", sub: "Per round" },
  ];
  const feedback = useMemo(() => {
    if (results.max_level >= 8) return "Outstanding — you're operating near the top of the ladder.";
    if (results.accuracy >= 80) return "Solid round of training. Keep going.";
    if (results.rounds_played < 4) return "Short session. Try a longer duration to build rhythm.";
    return "Good work. Keep training and watch your max level climb.";
  }, [results]);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Session complete</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-3">Nice work.</h1>
        <p className="text-sm text-muted-foreground">
          {(results.game_type || "master").toUpperCase()} · {duration}s · {fmtMs(speed)} item speed
        </p>
        {(() => {
          if (results.is_new_personal_best) {
            return { label: "NEW PERSONAL BEST", tint: "bg-primary/20 border-primary text-primary" };
          }
          if (results.is_new_monthly_best) {
            return { label: "NEW MONTHLY BEST", tint: "bg-purple-500/20 border-purple-400 text-purple-300" };
          }
          if (results.is_new_weekly_best) {
            return { label: "NEW WEEKLY BEST", tint: "bg-pink-500/20 border-pink-400 text-pink-300" };
          }
          return null;
        })() && (() => {
          const badge = results.is_new_personal_best
            ? { label: "NEW PERSONAL BEST", tint: "bg-primary/20 border-primary text-primary" }
            : results.is_new_monthly_best
            ? { label: "NEW MONTHLY BEST", tint: "bg-purple-500/20 border-purple-400 text-purple-300" }
            : { label: "NEW WEEKLY BEST", tint: "bg-pink-500/20 border-pink-400 text-pink-300" };
          return (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className={`inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full border font-bold text-sm ${badge.tint}`}
              data-testid="wm-npb-badge"
            >
              <Sparkles className="w-4 h-4" /> {badge.label}
            </motion.div>
          );
        })()}
      </div>
      <AchievementUnlocked results={results} />
      <div className="text-center mb-8 p-8 bg-card/60 border border-border" data-testid="wm-rating-summary">
        <div className="font-mono text-6xl text-primary mb-2" data-testid="wm-rating">
          {Math.round(results.rating ?? (results.max_level || 0) * 10)}
        </div>
        <div className="flex items-center justify-center gap-2">
          <p className="text-muted-foreground">Rating <span className="text-muted-foreground/60">/ 150</span></p>
          <RatingPanel mode="working_memory" itemsCount={duration} />
        </div>
        <p className="text-sm text-foreground/80 mt-1" data-testid="wm-rating-band">
          {results.rating_band || `level ${results.max_level} of 10`}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <Card key={c.label} className="bg-card/50 border-border">
            <CardContent className="p-5">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{c.label}</p>
              <p className="font-heading text-3xl font-bold text-primary" data-testid={`wm-result-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>{c.value}</p>
              {c.sub && <p className="text-xs text-muted-foreground mt-2">{c.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="bg-card/50 border-border mb-8">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground leading-relaxed">{feedback}</p>
        </CardContent>
      </Card>
      <div className="flex flex-wrap gap-3">
        <Button onClick={onRestart} className="flex-1 sm:flex-initial" data-testid="wm-train-again">
          <RotateCcw className="w-4 h-4 mr-2" /> Train again
        </Button>
        <Button variant="outline" onClick={() => navigate("/leaderboard?mode=working_memory")}>
          <Trophy className="w-4 h-4 mr-2" /> Leaderboard
        </Button>
        <Button variant="ghost" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
        </Button>
      </div>
    </div>
  );
};

export default WorkingMemoryTraining;
